import { useEffect, useState } from "react";
import {
  fetchAdminFaqs,
  createAdminFaq,
  deleteAdminFaq,
} from "@/api/auth";

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({
    question: "",
    answer: "",
    order: 0,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedFaqs, setExpandedFaqs] = useState({});

  // تحميل FAQ
  const loadFaqs = async () => {
    try {
      const data = await fetchAdminFaqs();
      setFaqs(data.sort((a, b) => a.order - b.order));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadFaqs();
  }, []);

  // إضافة/تعديل FAQ
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const faqData = {
        question: form.question.trim(),
        answer: form.answer.trim(),
        order: Number(form.order) || 0,
      };

      if (editingId) {
        // تعديل FAQ موجود
        // await updateAdminFaq(editingId, faqData); // تحتاج لإضافة هذه الدالة في API
      } else {
        // إضافة FAQ جديد
        await createAdminFaq(faqData);
      }

      setForm({
        question: "",
        answer: "",
        order: 0,
      });
      setEditingId(null);
      loadFaqs();
    } catch (e) {
      setError("فشل حفظ السؤال");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // حذف FAQ
  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا السؤال؟")) return;

    try {
      await deleteAdminFaq(id);
      loadFaqs();
    } catch (e) {
      console.error(e);
      setError("فشل حذف السؤال");
    }
  };

  // تحرير FAQ
  const handleEdit = (faq) => {
    setForm({
      question: faq.question,
      answer: faq.answer,
      order: faq.order,
    });
    setEditingId(faq.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // تبديل توسيع/طي FAQ
  const toggleExpand = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="faqs-container">
      <div className="faqs-header">
        <h1 className="faqs-title">إدارة الأسئلة الشائعة</h1>
        <p className="faqs-subtitle">أضف، عدّل، أو احذف الأسئلة الشائعة في موقعك</p>
      </div>

      {/* نموذج الإضافة/التعديل */}
      <div className="faqs-form-container">
        <h2 className="form-title">
          {editingId ? "تعديل السؤال" : "إضافة سؤال جديد"}
        </h2>
        
        <form onSubmit={handleSubmit} className="faqs-form">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                السؤال *
                <span className="label-hint">أدخل السؤال بوضوح</span>
              </label>
              <input
                className="form-input"
                placeholder="مثال: كيف يمكنني إنشاء حساب جديد؟"
                value={form.question}
                onChange={(e) =>
                  setForm({ ...form, question: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                الإجابة *
                <span className="label-hint">اكتب إجابة واضحة ومفصلة</span>
              </label>
              <textarea
                className="form-textarea"
                placeholder="مثال: لإنشاء حساب جديد، اضغط على زر 'تسجيل' في أعلى الصفحة..."
                value={form.answer}
                onChange={(e) =>
                  setForm({ ...form, answer: e.target.value })
                }
                required
                rows={6}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                الترتيب
                <span className="label-hint">رقم يحدد ترتيب ظهور السؤال (اختياري)</span>
              </label>
              <input
                className="form-input"
                type="number"
                placeholder="0"
                value={form.order}
                onChange={(e) =>
                  setForm({ ...form, order: e.target.value })
                }
                min="0"
              />
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  جاري الحفظ...
                </>
              ) : editingId ? "حفظ التعديلات" : "إضافة السؤال"}
            </button>
            
            {editingId && (
              <button 
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setForm({ question: "", answer: "", order: 0 });
                  setEditingId(null);
                }}
              >
                إلغاء التعديل
              </button>
            )}
          </div>

          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
        </form>
      </div>

      {/* قائمة الأسئلة */}
      <div className="faqs-list-container">
        <div className="list-header">
          <h2 className="list-title">الأسئلة المضافة ({faqs.length})</h2>
          <div className="stats">
            <span className="stat-item">ترتيب: من {Math.min(...faqs.map(f => f.order))} إلى {Math.max(...faqs.map(f => f.order))}</span>
          </div>
        </div>

        {faqs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">❓</div>
            <h3 className="empty-title">لا توجد أسئلة مضافة بعد</h3>
            <p className="empty-subtitle">ابدأ بإضافة أول سؤال شائع</p>
          </div>
        ) : (
          <div className="faqs-grid">
            {faqs.map((faq) => (
              <div 
                key={faq.id} 
                className={`faq-card ${editingId === faq.id ? "editing" : ""}`}
              >
                <div 
                  className="faq-header"
                  onClick={() => toggleExpand(faq.id)}
                >
                  <div className="faq-meta">
                    <span className="faq-order">#{faq.order}</span>
                    <h3 className="faq-question">{faq.question}</h3>
                  </div>
                  <button 
                    className="expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(faq.id);
                    }}
                  >
                    {expandedFaqs[faq.id] ? "▲" : "▼"}
                  </button>
                </div>

                <div 
                  className={`faq-content ${expandedFaqs[faq.id] ? "expanded" : ""}`}
                >
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>

                  <div className="faq-actions">
                    <button 
                      className="action-btn edit-btn"
                      onClick={() => handleEdit(faq)}
                    >
                      <span className="action-icon">✏️</span>
                      تعديل
                    </button>
                    <button 
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(faq.id)}
                    >
                      <span className="action-icon">🗑️</span>
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .faqs-container {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .faqs-header {
          text-align: center;
          margin-bottom: 3rem;
          padding-bottom: 2rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .faqs-title {
          color: #1e293b;
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .faqs-subtitle {
          color: #64748b;
          font-size: 1.1rem;
          max-width: 600px;
          margin: 0 auto;
        }

        .faqs-form-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 3rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .form-title {
          color: #1e293b;
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #3b82f6;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group:nth-child(2) {
          grid-column: 1 / -1;
        }

        .form-label {
          color: #334155;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .label-hint {
          color: #64748b;
          font-weight: normal;
          font-size: 0.875rem;
        }

        .form-input {
          padding: 0.875rem 1rem;
          border: 2px solid #cbd5e1;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: #f8fafc;
        }

        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          background: white;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-textarea {
          padding: 1rem;
          border: 2px solid #cbd5e1;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: #f8fafc;
          resize: vertical;
          font-family: inherit;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          background: white;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .submit-btn {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          border: none;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 160px;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .cancel-btn {
          background: #f1f5f9;
          color: #64748b;
          border: 2px solid #cbd5e1;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-btn:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .error-alert {
          background: #fef2f2;
          color: #dc2626;
          padding: 1rem;
          border-radius: 10px;
          border: 1px solid #fecaca;
          margin-top: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .error-icon {
          font-size: 1.2rem;
        }

        .faqs-list-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .list-title {
          color: #1e293b;
          font-size: 1.5rem;
          margin: 0;
        }

        .stats {
          display: flex;
          gap: 1rem;
        }

        .stat-item {
          color: #64748b;
          font-size: 0.875rem;
          background: #f1f5f9;
          padding: 0.5rem 1rem;
          border-radius: 20px;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.3;
        }

        .empty-title {
          color: #475569;
          margin-bottom: 0.5rem;
        }

        .empty-subtitle {
          color: #94a3b8;
        }

        .faqs-grid {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .faq-card {
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .faq-card:hover {
          border-color: #cbd5e1;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
        }

        .faq-card.editing {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .faq-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          cursor: pointer;
          background: #f8fafc;
        }

        .faq-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex: 1;
        }

        .faq-order {
          background: #3b82f6;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
          flex-shrink: 0;
        }

        .faq-question {
          color: #1e293b;
          font-size: 1.125rem;
          margin: 0;
          font-weight: 600;
          flex: 1;
        }

        .expand-btn {
          background: none;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #64748b;
          transition: all 0.3s ease;
          font-size: 0.875rem;
        }

        .expand-btn:hover {
          background: #e2e8f0;
          color: #475569;
        }

        .faq-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }

        .faq-content.expanded {
          max-height: 500px;
        }

        .faq-answer {
          padding: 1.5rem;
          border-top: 1px solid #e2e8f0;
          background: white;
        }

        .faq-answer p {
          color: #475569;
          line-height: 1.8;
          margin: 0;
          white-space: pre-line;
        }

        .faq-actions {
          padding: 1rem 1.5rem;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }

        .action-btn {
          padding: 0.5rem 1.25rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border: none;
        }

        .edit-btn {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .edit-btn:hover {
          background: #bfdbfe;
        }

        .delete-btn {
          background: #fee2e2;
          color: #dc2626;
        }

        .delete-btn:hover {
          background: #fecaca;
        }

        .action-icon {
          font-size: 1rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .faqs-container {
            padding: 1rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .submit-btn, .cancel-btn {
            width: 100%;
            justify-content: center;
          }

          .list-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .faq-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .faq-meta {
            width: 100%;
          }

          .expand-btn {
            align-self: flex-end;
          }
        }

        @media (max-width: 480px) {
          .faqs-title {
            font-size: 2rem;
          }

          .faqs-form-container,
          .faqs-list-container {
            padding: 1.5rem;
          }

          .faq-actions {
            flex-direction: column;
          }

          .action-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}